import axiosInstance from '@/api/apiClient'
import type { MyPrepStatus } from '../redux/types/charterTypes'

// ──────────────────────────────────────────────
// Config
// ──────────────────────────────────────────────

export const getCharterConfig = (roomId: string) =>
  axiosInstance.get(`/oge/rooms/${roomId}/charter/config/`)

export const updateCharterConfig = (roomId: string, data: Record<string, unknown>) =>
  axiosInstance.put(`/oge/rooms/${roomId}/charter/config/`, data)

// ──────────────────────────────────────────────
// Status
// ──────────────────────────────────────────────

export const getCharterStatus = (roomId: string) =>
  axiosInstance.get(`/oge/rooms/${roomId}/charter/status/`)

// Everyone in the room, placed or not. The status call above walks the team
// list, so a student without a team appears nowhere in it.
export const getCharterRoster = (roomId: string) =>
  axiosInstance.get(`/oge/rooms/${roomId}/charter/roster/`)

export const getCharterGateStatus = (roomId: string) =>
  axiosInstance.get(`/oge/rooms/${roomId}/charter/gate-status/`)

// ──────────────────────────────────────────────
// Session
// ──────────────────────────────────────────────

export const getCharterSession = (roomId: string) =>
  axiosInstance.get(`/oge/rooms/${roomId}/charter/session/`)

// The student's own prep progress, answered whether or not they have a team.
// The session call above 404s until they are placed.
export const getMyPrepStatus = (roomId: string) =>
  axiosInstance.get<MyPrepStatus>(`/oge/rooms/${roomId}/charter/my-prep-status/`)

// ──────────────────────────────────────────────
// Round 0 controls (professor, room-wide)
// ──────────────────────────────────────────────

export type CharterRoundAction =
  | 'start'
  | 'advance'
  | 'previous'
  | 'end'
  | 'reset'

export const charterRoundControl = (roomId: string, action: CharterRoundAction) =>
  axiosInstance.post(`/oge/rooms/${roomId}/charter/control/${action}/`)

// ──────────────────────────────────────────────
// Profile
// ──────────────────────────────────────────────

export const getMyProfile = (roomId: string) =>
  axiosInstance.get(`/oge/rooms/${roomId}/charter/my-profile/`)

export const approveMyProfile = (roomId: string) =>
  axiosInstance.post(`/oge/rooms/${roomId}/charter/my-profile/approve/`)

/** Student-accessible: public summaries for all team members (no private profile data). */
export const getMyTeamSummaries = (roomId: string) =>
  axiosInstance.get(`/oge/rooms/${roomId}/charter/my-team/`)

// ──────────────────────────────────────────────
// Export
// ──────────────────────────────────────────────

export const exportCharter = (
  roomId: string,
  teamId: number,
  version?: number,
  isDraft?: boolean
) =>
  axiosInstance.post(
    `/oge/rooms/${roomId}/teams/${teamId}/charter/export/`,
    { ...(version !== undefined && { version }), ...(isDraft && { is_draft: true }) },
    { responseType: 'blob' }
  )

// ──────────────────────────────────────────────
// Versions
// ──────────────────────────────────────────────

export const getCharterVersions = (roomId: string, teamId: number) =>
  axiosInstance.get(`/oge/rooms/${roomId}/teams/${teamId}/charter/versions/`)

// ──────────────────────────────────────────────
// Renegotiation (Phase 3)
// ──────────────────────────────────────────────

export const createRenegotiationRequest = (
  roomId: string,
  teamId: number,
  data: { sections_to_amend: string[]; reason: string }
) => axiosInstance.post(`/oge/rooms/${roomId}/teams/${teamId}/charter/renegotiate/`, data)

export const voteRenegotiation = (roomId: string, teamId: number, vote: boolean) =>
  axiosInstance.post(`/oge/rooms/${roomId}/teams/${teamId}/charter/renegotiate/vote/`, {
    vote,
  })

export const cancelRenegotiation = (roomId: string, teamId: number) =>
  axiosInstance.post(
    `/oge/rooms/${roomId}/teams/${teamId}/charter/renegotiate/cancel/`,
    {}
  )

// ──────────────────────────────────────────────
// Prep chat history (Phase 2 read-only view)
// ──────────────────────────────────────────────

export const getPrepHistory = (roomId: string) =>
  axiosInstance.get(`/oge/rooms/${roomId}/charter/my-prep-history/`)
